/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useEffect, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';

import { Table, Button, Alert } from 'antd';
import { RightOutlined } from '@ant-design/icons';

import EditAlphaOpicData from './editAlphaOpicData';
import { AlphaOpicPlot } from 'components/plots';

export const Luox = ({
  luoxData,
  setFormattedLuoxData,
  measurementName,
  showEditOptions,
}) => {
  const [showLuoxEditOptions, setShowLuoxEditOptions] = useState(false);
  // const [radianceOrIrradiance, setRadianceOrIrradiance] = useState(
  //   "irradiance"
  // );

  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);
  const {
    absoluteOrRelative,
    radianceOrIrradiance,
    powerScale,
    areaScale,
    relativePower,
  } = luoxUnits;

  const isRadiance = radianceOrIrradiance === 'radiance';
  const endUnites = `${powerScale}.${
    areaScale === 'm²' ? 'm⁻²' : areaScale === 'cm²' ? 'cm⁻²' : 'mm⁻²'
  }${isRadiance ? '.sr' : ''}`;
  const { luoxOutput } = luoxData;

  const columns = [
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 200,
      align: 'left',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 200,
      align: 'left',
    },
  ];

  const data = [
    {
      key: 1,
      quantity: isRadiance ? 'Luminance [cd/m²]' : 'Illuminance [lx]',
      value: luoxOutput.luminanceTotals[0].toFixed(4),
    },
    // {
    //   key: 2,
    //   quantity: 'CIE 1931 xy chromaticity (x)',
    //   value: luoxOutput.chromaticity31[0].x.toFixed(4),
    // },
    // {
    //   key: 3,
    //   quantity: 'CIE 1931 xy chromaticity (y)',
    //   value: luoxOutput.chromaticity31[0].y.toFixed(4),
    // },
    // {
    //   key: 4,
    //   quantity: 'CIE 1964 x₁₀y₁₀ chromaticity (x₁₀)',
    //   value: luoxOutput.chromaticity64[0].x.toFixed(4),
    // },
    // {
    //   key: 5,
    //   quantity: 'CIE 1964 x₁₀y₁₀ chromaticity (y₁₀)',
    //   value: luoxOutput.chromaticity64[0].y.toFixed(4),
    // },
    {
      key: 2,
      quantity: `S-cone-opic ${
        isRadiance ? `radiance` : `irradiance`
      } (${endUnites})`,
      value: luoxOutput.sConeTotals[0].toFixed(4),
    },
    {
      key: 3,
      quantity: `M-cone-opic ${
        isRadiance ? `radiance` : `irradiance`
      } (${endUnites})`,
      value: luoxOutput.mConeTotals[0].toFixed(4),
    },
    {
      key: 4,
      quantity: `L-cone-opic ${
        isRadiance ? `radiance` : `irradiance`
      } (${endUnites})`,
      value: luoxOutput.lConeTotals[0].toFixed(4),
    },
    {
      key: 5,
      quantity: `Rhodopic ${
        isRadiance ? `radiance` : `irradiance`
      } (${endUnites})`,
      value: luoxOutput.rodTotals[0].toFixed(4),
    },
    {
      key: 6,
      quantity: `Melanopic ${
        isRadiance ? `radiance` : `irradiance`
      } (${endUnites})`,
      value: luoxOutput.melTotals[0].toFixed(4),
    },
    {
      key: 7,
      quantity: `S-cone-opic ED${isRadiance ? 'L [cd/m²]' : 'I [lx]'}`,
      value: luoxOutput.equivalentDaylightAlphaOpic.sc[0].toFixed(4),
    },
    {
      key: 8,
      quantity: `M-cone-opic ED${isRadiance ? 'L [cd/m²]' : 'I [lx]'}`,
      value: luoxOutput.equivalentDaylightAlphaOpic.mc[0].toFixed(4),
    },
    {
      key: 9,
      quantity: `L-cone-opic ED${isRadiance ? 'L [cd/m²]' : 'I [lx]'}`,
      value: luoxOutput.equivalentDaylightAlphaOpic.lc[0].toFixed(4),
    },
    {
      key: 10,
      quantity: `Rhodopic ED${isRadiance ? 'L [cd/m²]' : 'I [lx]'}`,
      value: luoxOutput.equivalentDaylightAlphaOpic.rh[0].toFixed(4),
    },
    {
      key: 11,
      quantity: `Melanopic ED${isRadiance ? 'L [cd/m²]' : 'I [lx]'}`,
      value: luoxOutput.equivalentDaylightAlphaOpic.mel[0].toFixed(4),
    },
    {
      key: 12,
      quantity: 'S-cone-opic ELR',
      value: luoxOutput.alphaOpicEfficiency.sc[0].toFixed(4),
    },
    {
      key: 13,
      quantity: 'M-cone-opic ELR',
      value: luoxOutput.alphaOpicEfficiency.mc[0].toFixed(4),
    },
    {
      key: 14,
      quantity: 'L-cone-opic ELR',
      value: luoxOutput.alphaOpicEfficiency.lc[0].toFixed(4),
    },
    {
      key: 15,
      quantity: 'Rhodopic ELR',
      value: luoxOutput.alphaOpicEfficiency.rh[0].toFixed(4),
    },
    {
      key: 16,
      quantity: 'Melanopic ELR',
      value: luoxOutput.alphaOpicEfficiency.mel[0].toFixed(4),
    },
  ];

  useEffect(() => {
    setFormattedLuoxData(data);
  }, []);

  const handleLuoxEditOptionsToggle = () => {
    const toggle = !showLuoxEditOptions;
    setShowLuoxEditOptions(toggle);
  };

  return (
    <Fragment>
      {/* {showEditButton && (
        <Button
          type="primary"
          onClick={() => setShowLuoxModal(true)}
          style={{ marginBottom: "1rem" }}
        >
          Edit Luox Calculations
        </Button>
      )} */}
      {showEditOptions && (
        <Alert
          message={
            <p css={luoxNoteStyles}>
              Alpha-Opic calculations are performed by Luox App{' '}
              <a
                href="https://luox.app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click to visit
              </a>
            </p>
          }
          showIcon
          closable
        />
      )}
      {showEditOptions && (
        <div>
          <div css={luoxHeadContainer} onClick={handleLuoxEditOptionsToggle}>
            <h5>Edit calculation</h5>
            <RightOutlined css={toggleIconStyle(showLuoxEditOptions)} />
          </div>
          <div css={luoxEditDataContainerStyle(showLuoxEditOptions)}>
            <EditAlphaOpicData
              showEditOptions={showEditOptions}
              measurementName={measurementName}
            />
          </div>
        </div>
      )}
      <AlphaOpicPlot luoxData={luoxData} luoxUnits={luoxUnits} />
      <br />
      <Table
        style={{ marginBottom: '2rem' }}
        columns={columns}
        dataSource={data}
        bordered
        pagination={false}
        scroll={{ y: 500 }}
      />
    </Fragment>
  );
};

const luoxHeadContainer = css`
  display: inline-block;
  cursor: pointer;

  h5 {
    display: inline-block;
  }

  span {
    vertical-align: initial;
  }
`;

const luoxEditDataContainerStyle = (showLuoxEditOptions) => css`
  opacity: ${showLuoxEditOptions ? '1' : '0'};
  pointer-events: ${showLuoxEditOptions ? 'auto' : 'none'};
  height: ${showLuoxEditOptions ? '10rem' : '1rem'};

  transition: height 400ms;
`;

const toggleIconStyle = (showLuoxEditOptions) => css`
  margin-left: 4px;

  svg {
    width: 10px;
    height: 10px;

    ${showLuoxEditOptions &&
    css`
      transform: rotate(90deg);
    `};
    transition: all 200ms;
  }
`;

const luoxNoteStyles = css`
  margin: 0;
`;
